import userIcon from "@/assets/icons/user.svg";
import { ProfileInfoForm } from "@/features/profile/ProfileInfoForm";
import { PasswordCard } from "@/features/profile/PasswordCard";
import { SectionTitle } from "@/components/common/SectionTitle";
import { EInvoiceForm } from "@/features/profile/EInvoiceForm";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/auth.store";
import { GetProfile } from "@/services/account.service";
import { GetDialCode } from "@/services/dialcode.service";
import type { ProfileData } from "@/types/account/profile";
import type { DialCode } from "@/types/dialcode";
import { ProfileSkeleton } from "./ProfileSkeleton";

export default function ProfilePage() {
  const [loading, setLoading] = useState(true);
  const [loadingDialCodes, setLoadingDialCodes] = useState(false);
  const [dialCodes, setDialCodes] = useState<DialCode[]>([]);
  const [profileData, setProfileData] =
    useState<ProfileData | null>(null);

  // Initial profile fetch
  useEffect(() => {
    const fetchInitialProfile = async () => {
      try {
        const agentCompanySellerId =
          useAuthStore.getState().user?.agentCompanySellerId;

        if (!agentCompanySellerId) {
          toast.error("Agent company seller ID is missing.");
          return;
        }

        const response = await GetProfile({
          agentCompanySellerId,
        });

        setProfileData(response.data);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialProfile();
  }, []);

  useEffect(() => {
    const fetchDialCodes = async () => {
      try {
        setLoadingDialCodes(true);

        const response = await GetDialCode();

        setDialCodes(response.data?.dialCodeList ?? []);
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to get dial codes.",
        );
      } finally {
        setLoadingDialCodes(false);
      }
    };

    fetchDialCodes();
  }, []);

  // Refresh profile after update
  const refreshProfile = async () => {
    try {
      const agentCompanySellerId =
        useAuthStore.getState().user?.agentCompanySellerId;

      if (!agentCompanySellerId) {
        toast.error("Agent company seller ID is missing.");
        return;
      }

      const response = await GetProfile({
        agentCompanySellerId,
      });

      setProfileData(response.data);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to refresh profile details.",
      );
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pt-4 pb-16 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between">
        <SectionTitle img={userIcon} title="Profile" />
      </div>

      <div className="mt-6">
        {loading ? (
          <ProfileSkeleton />
        ) : (
          <div className="flex flex-col gap-6">
            <ProfileInfoForm
              profileData={profileData}
              dialCodes={dialCodes}
              loadingDialCodes={loadingDialCodes}
              onUpdated={refreshProfile}
            />

            <PasswordCard lastChangedPassword={profileData?.lastChangedPasswordDateTime ?? ""}/>

            <EInvoiceForm
              eInvoice={profileData?.eInvoice}
              dialCodes={dialCodes}
              loadingDialCodes={loadingDialCodes}
            />
          </div>
        )}
      </div>
    </div>
  );
}